import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { ROLE_LABELS } from "@/context/AuthContext";

export default function Utenti() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/users").then((r) => setUsers(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    api.get("/users/roles").then((r) => setRoles(r.data));
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ username: "", nome: "", email: "", role: "traffico", password: "", attivo: true });
    setOpen(true);
  };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ username: u.username, nome: u.nome, email: u.email || "", role: u.role, password: "", attivo: u.attivo !== false });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        const payload = { nome: form.nome, email: form.email, role: form.role, attivo: form.attivo };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing.id}`, payload);
        toast.success("Utente aggiornato");
      } else {
        if (!form.username || !form.password) {
          toast.error("Username e password obbligatori");
          setSaving(false);
          return;
        }
        await api.post("/users", form);
        toast.success("Utente creato");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/users/${toDelete.id}`);
      toast.success("Utente eliminato");
      setToDelete(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-4" data-testid="utenti-page">
      <div className="flex justify-end">
        <Button onClick={openNew} data-testid="user-new">
          <Plus className="h-4 w-4 mr-1.5" />
          Nuovo utente
        </Button>
      </div>
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-[11px] uppercase tracking-wider text-slate-500">
              <th className="text-left px-4 py-2.5">Username</th>
              <th className="text-left px-4 py-2.5">Nome</th>
              <th className="text-left px-4 py-2.5">Email</th>
              <th className="text-left px-4 py-2.5">Ruolo</th>
              <th className="text-left px-4 py-2.5">Stato</th>
              <th className="px-4 py-2.5" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="py-10 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-primary" /></td></tr>
            ) : (
              users.map((u) => (
                <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`user-row-${u.username}`}>
                  <td className="px-4 py-2.5 font-medium text-slate-800">{u.username}</td>
                  <td className="px-4 py-2.5">{u.nome}</td>
                  <td className="px-4 py-2.5 text-slate-500">{u.email || "—"}</td>
                  <td className="px-4 py-2.5">
                    <Badge variant="outline" className="bg-sky-50 text-sky-700 border-sky-200">
                      {ROLE_LABELS[u.role] || u.role}
                    </Badge>
                  </td>
                  <td className="px-4 py-2.5">
                    {u.attivo !== false ? (
                      <span className="text-green-700 text-xs font-medium">Attivo</span>
                    ) : (
                      <span className="text-slate-400 text-xs">Disattivo</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-slate-500 hover:text-sky-700" onClick={() => openEdit(u)} data-testid={`user-edit-${u.username}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-slate-500 hover:text-red-600" onClick={() => setToDelete(u)} data-testid={`user-delete-${u.username}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="user-form">
          <DialogHeader>
            <DialogTitle className="font-head">{editing ? "Modifica utente" : "Nuovo utente"}</DialogTitle>
            <DialogDescription>Definisci username, ruolo e credenziali di accesso.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div>
              <Label className="text-xs">Username *</Label>
              <Input className="mt-1" value={form.username || ""} disabled={!!editing} onChange={(e) => setForm({ ...form, username: e.target.value })} data-testid="user-username" />
            </div>
            <div>
              <Label className="text-xs">Nome completo</Label>
              <Input className="mt-1" value={form.nome || ""} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="user-nome" />
            </div>
            <div>
              <Label className="text-xs">Email</Label>
              <Input className="mt-1" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Ruolo *</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="mt-1" data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {roles.map((r) => (<SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{editing ? "Nuova password (lascia vuoto per non cambiare)" : "Password *"}</Label>
              <Input type="password" className="mt-1" value={form.password || ""} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" />
            </div>
            {editing && (
              <div className="flex items-center justify-between border border-slate-200 rounded-md px-3 h-10">
                <Label>Attivo</Label>
                <Switch checked={!!form.attivo} onCheckedChange={(c) => setForm({ ...form, attivo: c })} />
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
              <Button type="submit" disabled={saving} data-testid="user-submit">
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Salva
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare l'utente {toDelete?.username}?</AlertDialogTitle>
            <AlertDialogDescription>L'utente non potrà più accedere.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="user-delete-confirm">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
